import formatDate from 'date-fns/format';
import {
  CalendarExpirationAttributes,
  ExpirationType,
  LevelAttributes,
  LevelsConfigAttributes,
} from '@loyalty/shared-common/gamification-api-client';
import { MembershipAttributes } from '@loyalty/shared-common/profile-api-client';
import {
  defaultLevelImage,
  getLocalizedProperty,
  getNextExpirationDate,
  toEndOfDay,
} from '@loyalty/shared-common';

export function getLevelImage(level: LevelAttributes | undefined, locale?: string) {
  if (!level) {
    return defaultLevelImage;
  }
  return getLocalizedProperty(level, 'image', locale) || defaultLevelImage;
}

export function renderLevelExpiration(
  levelConfig: LevelsConfigAttributes | undefined,
  translationHelpers: { locale: string; t: (key: string, params?: any) => string },
  options: {
    currentLevel?: LevelAttributes;
    membership: MembershipAttributes;
    nextLevel?: LevelAttributes;
  },
  context: 'backoffice' | 'portal',
): string {
  if (!levelConfig || levelConfig.expiration.type === ExpirationType.NONE) {
    return '';
  }
  const { t, locale } = translationHelpers;
  const { currentLevel, nextLevel, membership } = options;
  const expirationType = levelConfig.expiration?.type;
  const expirationMode = (levelConfig.expiration as CalendarExpirationAttributes)?.expirationMode;

  const dateFormat = t('dateFormat') ?? 'dd/MM/yyyy';
  // maybe always calling getNextExpiration ? It may fix the timezone issue...?
  const expirationDate = membership.level?.expireAt
    ? formatDate(toEndOfDay(new Date(membership.level?.expireAt), levelConfig.timezone), dateFormat)
    : formatDate(getNextExpirationDate(levelConfig.expiration, levelConfig.timezone)!, dateFormat);
  const currentLevelName = getLocalizedProperty(currentLevel, 'name', locale);
  const nextLevelName = getLocalizedProperty(nextLevel, 'name', locale);

  switch (`${context}:${!!currentLevel}:${!!nextLevel}:${expirationMode}:${expirationType}`) {
    // backoffice
    case 'backoffice:false:true:REEVALUATE:CALENDAR':
    case 'backoffice:false:true:REEVALUATE:END_OF_MONTH':
    case 'backoffice:false:true:RESET:CALENDAR':
    case 'backoffice:false:true:RESET:END_OF_MONTH':
    case 'backoffice:false:true:RESET:END_OF_WEEK':
      return t('backoffice.level.expiration.progression.drop', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Member's progression towards level {{nextLevelName}} will be lost on {{expirationDate}}.

    case 'backoffice:false:true:REVALUATE:ROLLING':
    case 'backoffice:false:true:RESET:ROLLING':
      return t('backoffice.level.expiration.progression.reevaluate.unless', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Member must reach level {{nextLevelName}} before {{expirationDate}} or his progression will be lost.

    case 'backoffice:true:true:REEVALUATE:CALENDAR':
    case 'backoffice:true:false:REEVALUATE:CALENDAR':
    case 'backoffice:true:true:REEVALUATE:END_OF_MONTH':
    case 'backoffice:true:false:REEVALUATE:END_OF_MONTH':
    case 'backoffice:true:false:REEVALUATE:END_OF_WEEK':
    case 'backoffice:true:false:REEVALUATE:ROLLING':
      return t('backoffice.level.expiration.level.reevaluate', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Member's level {{currentLevelName}} will be reevaluated on {{expirationDate}}.

    case 'backoffice:true:true:REEVALUATE:ROLLING':
      return t('backoffice.level.expiration.level.reevaluate.unless', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Member's level {{currentLevelName}} will be reevaluated on {{expirationDate}} unless he reaches level {{nextLevel}}.

    case 'backoffice:true:true:RESET:CALENDAR':
    case 'backoffice:true:false:RESET:CALENDAR':
    case 'backoffice:true:true:RESET:END_OF_MONTH':
    case 'backoffice:true:false:RESET:END_OF_MONTH':
    case 'backoffice:true:true:RESET:END_OF_WEEK':
    case 'backoffice:true:false:RESET:END_OF_WEEK':
    case 'backoffice:true:false:RESET:ROLLING':
      return t('backoffice.level.expiration.level.drop', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Member's level {{currentLevelName}} will be lost on {{expirationDate}}.

    case 'backoffice:true:true:RESET:ROLLING':
      return t('backoffice.level.expiration.level.drop.unless', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Member's level {{currentLevelName}} will be lost on {{expirationDate}} unless he reaches level {{nextLevelName}}.

    // portal
    case 'portal:false:true:REEVALUATE:CALENDAR':
    case 'portal:false:true:REEVALUATE:END_OF_MONTH':
    case 'portal:false:true:REEVALUATE:END_OF_WEEK':
    case 'portal:false:true:RESET:CALENDAR':
    case 'portal:false:true:RESET:END_OF_MONTH':
    case 'portal:false:true:RESET:END_OF_WEEK':
      return t('portal.level.expiration.progression.drop', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Your progression towards level {{nextLevelName}} will be lost on {{expirationDate}}.

    case 'portal:false:true:REVALUATE:ROLLING':
    case 'portal:false:true:RESET:ROLLING':
      return t('portal.level.expiration.progression.reevaluate.unless', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Reach level {{nextLevelName}} before {{expirationDate}} or your progression will be lost.

    case 'portal:true:true:REEVALUATE:CALENDAR':
    case 'portal:true:false:REEVALUATE:CALENDAR':
    case 'portal:true:true:REEVALUATE:END_OF_MONTH':
    case 'portal:true:false:REEVALUATE:END_OF_MONTH':
    case 'portal:true:true:REEVALUATE:END_OF_WEEK':
    case 'portal:true:false:REEVALUATE:END_OF_WEEK':
    case 'portal:true:false:REEVALUATE:ROLLING':
      return t('portal.level.expiration.level.reevaluate', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Your level {{currentLevelName}} will be reevaluated on {{expirationDate}}.

    case 'portal:true:true:REEVALUATE:ROLLING':
      return t('portal.level.expiration.level.reevaluate.unless', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Your level {{currentLevelName}} will be reevaluated on {{expirationDate}} unless you reach level {{nextLevelName}}.

    case 'portal:true:true:RESET:CALENDAR':
    case 'portal:true:false:RESET:CALENDAR':
    case 'portal:true:true:RESET:END_OF_MONTH':
    case 'portal:true:false:RESET:END_OF_MONTH':
    case 'portal:true:true:RESET:END_OF_WEEK':
    case 'portal:true:false:RESET:END_OF_WEEK':
    case 'portal:true:false:RESET:ROLLING':
      return t('portal.level.expiration.level.drop', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Your level {{currentLevelName}} will be lost on {{expirationDate}}.

    case 'portal:true:true:RESET:ROLLING':
      return t('portal.level.expiration.level.drop.unless', {
        currentLevelName,
        nextLevelName,
        expirationDate,
      }); // Your level {{currentLevelName}} will be lost on {{expirationDate}} unless you reach level {{nextLevelName}}.

    default:
      return '';
  }
}
